import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { CountUp } from './CountUp';
import { Emoji } from './Emoji';
import { scoreToIndex, tileColor, tileTextColor } from './score-math';
import { Badge } from './Stamp';
import { cn, TEAM_COLOR, type Team } from './util';

export type Verdict = 'kelepir' | 'kaziklandi';

export interface BoughtItem {
  /** Stable per item (the content item id): it keys the fly-in / reorder animations. */
  id: string;
  emoji: string;
  name: string;
  price: number;
  /** Jury score once revealed; null/undefined while hidden. */
  score?: number | null;
  verdict?: Verdict | null;
  /** Was auto-assigned for 0 (slots full / broke). */
  auto?: boolean;
}

export interface BoughtListProps {
  items: readonly BoughtItem[];
  team: Team;
  slots?: number;
  /** Dashed rows for the empty slots (default true; the count stays in the heading either way). */
  placeholders?: boolean;
  /** Heading (default "ALDIKLARI"). Pass null to hide. */
  title?: string | null;
  /** Side new rows fly in from. Defaults to the ring centre: A (left corner) → 'right', B → 'left'. */
  flyFrom?: 'left' | 'right';
  /** Animate score count-up for newly revealed scores. */
  animateScores?: boolean;
  className?: string;
}

export const VERDICT_LABEL: Record<Verdict, string> = {
  kelepir: 'KELEPİR',
  kaziklandi: 'KAZIKLANDI',
};

/** Score chip coloured on the jury scale (red → yellow → green). */
export function ScoreChip({
  score,
  animate,
  className,
}: {
  score: number;
  animate?: boolean;
  className?: string;
}) {
  const i = scoreToIndex(score);
  return (
    <span
      className={cn(
        'comic inline-flex min-w-11 items-center justify-center border-2 border-ink px-1 pt-[3px] pb-px text-[18px] shadow-ink-xs',
        className,
      )}
      style={{ backgroundColor: tileColor(i), color: tileTextColor(i) }}
      title={`Jüri puanı ${score.toFixed(1)}`}
    >
      {animate ? <CountUp value={score} duration={1.2} /> : score.toFixed(1)}
    </span>
  );
}

/**
 * A buyer's shelf: one row per bought item (emoji, name, price, revealed score) + dashed empty slots.
 * Append to `items` to make a row fly in from the ring centre; set `score` (and `verdict`) later to
 * reveal it with a count-up and a wobbling KELEPİR / KAZIKLANDI badge. Rows past `slots` still render.
 */
export function BoughtList({
  items,
  team,
  slots = 5,
  placeholders = true,
  title = 'ALDIKLARI',
  flyFrom,
  animateScores = true,
  className,
}: BoughtListProps) {
  const reduce = useReducedMotion();
  const empty = placeholders ? Math.max(0, slots - items.length) : 0;
  const from = flyFrom ?? (team === 'A' ? 'right' : 'left');
  const dx = from === 'left' ? -160 : 160;
  return (
    <div className={cn('flex flex-col', className)}>
      {title && (
        <div className="comic mb-1.5 flex items-baseline justify-between text-[19px]">
          <span>{title}</span>
          <span className="tabular-nums">
            {items.length}/{slots}
          </span>
        </div>
      )}
      <ol className="flex flex-col gap-1.5">
        <AnimatePresence initial={false}>
          {items.map((it) => (
            <motion.li
              key={it.id}
              layout
              initial={
                reduce
                  ? { opacity: 0 }
                  : { opacity: 0, x: dx, y: -30, scale: 1.5, rotate: from === 'left' ? -10 : 10 }
              }
              animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 24 }}
              className="relative flex h-10 items-center gap-2 border-2 border-ink bg-white pr-1 pl-2 shadow-ink-xs"
            >
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 w-1.5 border-r-2 border-ink"
                style={{ backgroundColor: TEAM_COLOR[team] }}
              />
              <Emoji emoji={it.emoji} label="" size={24} className="ml-1.5" />
              <span className="min-w-0 flex-1 truncate text-[15px] font-black">{it.name}</span>
              {it.verdict && (
                <span className="absolute -top-3.5 right-12 z-10">
                  <Badge
                    tone={it.verdict === 'kelepir' ? 'green' : 'red'}
                    wobble
                    rotate={-5}
                    size="sm"
                  >
                    {VERDICT_LABEL[it.verdict]}
                  </Badge>
                </span>
              )}
              <span
                className="flex shrink-0 items-center gap-0.5 text-[14px] font-black tabular-nums"
                title={`${it.price} puana aldı`}
              >
                <Emoji emoji="💰" label="" size={14} />
                {it.price}
                {it.auto && (
                  <span className="ml-0.5 text-[10px] font-extrabold text-ink-soft">oto</span>
                )}
              </span>
              {it.score != null ? (
                <ScoreChip score={it.score} animate={animateScores} />
              ) : (
                <span className="comic inline-flex min-w-11 items-center justify-center border-2 border-dashed border-ink/50 pt-[3px] pb-px text-[18px] text-ink/40">
                  ?
                </span>
              )}
            </motion.li>
          ))}
        </AnimatePresence>
        {Array.from({ length: empty }, (_, i) => (
          <li
            key={`empty-${i}`}
            className="flex h-10 items-center border-2 border-dashed border-ink/35 px-3 text-[15px] font-bold text-ink/30"
          >
            —
          </li>
        ))}
      </ol>
    </div>
  );
}
